import { flow, types } from 'mobx-state-tree';
import {
  ApiTokenLifetime,
  DateTimeFormat,
  ServiceAccountDtoModelType,
} from './models';
import getAppRoot from './utils/getAppRoot';
import ServiceAccountsQuery from './queries/integration/ServiceAccountsQuery';
import { convertTimeStampToDateTimeString } from './utils/convertTimeStampToDateTimeString';
import CreateServiceAccountQuery from './queries/integration/CreateServiceAccountQuery';
import RevokeServiceAccountQuery from './queries/integration/RevokeServiceAccountQuery';

export type TokenStatus = 'active' | 'expiringSoon' | 'expired';

export interface ServiceAccounts {
  id?: string | null;
  fullname?: string | null;
  status: TokenStatus;
  expiresOn?: string | null;
  createdOn?: string | null;
  createdBy?: string | null;
}

const DEFAULT_CREATED_USER = 'Routific Support';

// A token is flagged "expiring soon" once it is within this window of its expiry.
const EXPIRING_SOON_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

export const getTokenStatus = (expiresOn?: string | null): TokenStatus => {
  if (!expiresOn) {
    // No expiry (tokens created before token expiry was introduced) — always active.
    return 'active';
  }
  const remainingMs = new Date(expiresOn).getTime() - Date.now();
  if (remainingMs <= 0) {
    return 'expired';
  }
  if (remainingMs <= EXPIRING_SOON_THRESHOLD_MS) {
    return 'expiringSoon';
  }
  return 'active';
};

const Integration = types.model('Integration')
  .props({
    isLoading: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get serviceAccountsCount(): number {
      return Array.from<ServiceAccountDtoModelType>(getAppRoot(self).model.serviceAccountDtos.values()).length;
    },
    get activeServiceAccountsCount(): number {
      return Array.from<ServiceAccountDtoModelType>(getAppRoot(self).model.serviceAccountDtos.values())
        .filter((serviceAccount) => getTokenStatus(serviceAccount.expiresOn) !== 'expired')
        .length;
    },
    get serviceAccounts(): ServiceAccounts[] {
      const use24Hour = getAppRoot(self).organization?.settings?.dateTimeFormat === DateTimeFormat.MILITARY;
      return Array.from<ServiceAccountDtoModelType>(getAppRoot(self).model.serviceAccountDtos.values())
        // Sort on the raw ISO `createdOn` (newest first) BEFORE formatting.
        .sort((a, b) => {
          const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
          const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
          return dateB - dateA;
        })
        .map((serviceAccount: ServiceAccountDtoModelType) => ({
          id: serviceAccount.uuid,
          fullname: serviceAccount.fullname || '',
          status: getTokenStatus(serviceAccount.expiresOn),
          expiresOn: serviceAccount.expiresOn ? convertTimeStampToDateTimeString(serviceAccount.expiresOn, use24Hour) : '',
          createdOn: serviceAccount.createdOn ? convertTimeStampToDateTimeString(serviceAccount.createdOn, use24Hour) : '',
          createdBy: serviceAccount.createdByUser?.fullname || DEFAULT_CREATED_USER,
        }));
    },
  }))

  .actions((self) => ({
    fetchServiceAccounts: flow(function* () {
      const fetchAccounts = async () => {
        const res = await getAppRoot(self).model.queryServiceAccountsForOrganization({}, ServiceAccountsQuery, { fetchPolicy: 'no-cache' }).currentPromise();
        return res.serviceAccountsForOrganization;
      };
      try {
        getAppRoot(self).model.clearServiceAccounts();
        self.isLoading = true;
        yield fetchAccounts();
      } finally {
        self.isLoading = false;
      }
      return Promise.resolve();
    }),
    createServiceAccount: flow(function* (name: string, expiresIn: ApiTokenLifetime) {
      const createAccount = async () => {
        const res = await getAppRoot(self).model.mutateCreateServiceAccount({ name, expiresIn }, CreateServiceAccountQuery).currentPromise();
        return res.createServiceAccount;
      };

      try {
        self.isLoading = true;
        const res = yield createAccount();
        return res.token;
      } catch (err) {
        return err;
      } finally {
        self.isLoading = false;
      }
    }),
    revokeServiceAccount: flow(function* (uuid: string) {
      const revokeServiceAccount = async () => {
        const res = await getAppRoot(self).model.mutateRevokeServiceAccount({ uuid }, RevokeServiceAccountQuery).currentPromise();
        return res.revokeServiceAccount;
      };

      try {
        self.isLoading = true;
        const res = yield revokeServiceAccount();
        return res.revoked;
      } finally {
        self.isLoading = false;
      }
    }),
    findServiceAccount(uuid: string) {
      return self.serviceAccounts.find(serviceAccount => serviceAccount.id === uuid);
    },
  }));

export default Integration;
